import useTranslation from "next-translate/useTranslation";
import { memo, useState } from "react";
import Cropper from "react-easy-crop";
import MolhamModalOffcanvas from "./MolhamModalOffCanvas";
import MolhamButton from "./ReuseableCompos/MolhamButton";
type Props = {
  onCropModalClose: () => void;
  showCropModal: boolean;
  image: string;
  onImageSubmit: (
    data: FormData,
    croppedImg: string,
    canvasScaled: HTMLCanvasElement
  ) => void;
  btnLabel?: string;
  isLoadingBtn?: boolean;
  isUploadingLoading?: boolean;
  progress?: number;
  onUploadBtnClick: (e: React.ChangeEvent<HTMLInputElement>) => void;
  formDataName?: string;
};
const EventImageCropperModal: React.FC<Props> = ({
  onCropModalClose,
  showCropModal,
  image,
  onImageSubmit,
  btnLabel,
  isUploadingLoading,
}) => {
  const { t } = useTranslation("common");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const onCropSubmit = () => {
    getCroppedImg(image, croppedAreaPixels).then((res: any) => {
      const { data, croppedImg, canvasScaled } = res;
      onImageSubmit(data, croppedImg, canvasScaled);
    });
  };
  return (
    <MolhamModalOffcanvas
      onHide={onCropModalClose}
      show={showCropModal}
      body={{ className: "p-0" }}
      header={{ label: "set_new_event_picture" }}
      fullscreen
      swipeToClose={false}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          height: 200,
        }}
      >
        <Cropper
          image={image}
          crop={crop}
          zoom={zoom}
          aspect={4 / 3}
          onCropChange={setCrop}
          onCropComplete={onCropComplete}
          transform=""
          style={{
            mediaStyle: {
              overflow: "unset",
            },
          }}
        />
      </div>

      <div className="pt-3 px-3">
        <label
          htmlFor="range-input"
          className="form-label d-flex justify-content-between p-0"
        >
          <span>{t(`zoom_out`)}</span>
          <span>{t(`zoom_in`)}</span>
        </label>
        <input
          type="range"
          value={zoom.toString()}
          min={1}
          max={3}
          step={0.1}
          aria-labelledby="Zoom"
          onChange={(e) => {
            setZoom(parseFloat(e.target.value));
          }}
          className="zoom-range w-100"
        />
      </div>
      <div className="p-3">
        <MolhamButton
          id="image-crop-btn"
          label={t(btnLabel) || t(`set`)}
          className="w-100"
          startIcon={"ai-upload"}
          onClick={onCropSubmit}
          isLoading={isUploadingLoading}
        />
      </div>
    </MolhamModalOffcanvas>
  );
};

export default memo(EventImageCropperModal);
export const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous"); // needed to avoid cross-origin issues on CodeSandbox
    image.src = url;
  });

export function getRadianAngle(degreeValue) {
  return (degreeValue * Math.PI) / 180;
}

/**
 * Returns the new bounding area of a rotated rectangle.
 */
export function rotateSize(width, height, rotation) {
  const rotRad = getRadianAngle(rotation);

  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 */
async function getCroppedImg(
  imageSrc,
  pixelCrop,
  rotation = 0,
  flip = { horizontal: false, vertical: false }
) {
  const image = (await createImage(imageSrc)) as HTMLImageElement;

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return null;
  }

  const rotRad = getRadianAngle(rotation);

  // calculate bounding box of the rotated image
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotation
  );

  // set canvas size to match the bounding box
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // translate canvas context to a central location to allow rotating and flipping around the center
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);

  // draw rotated image
  ctx.drawImage(image, 0, 0);

  const croppedCanvas = document.createElement("canvas");

  const croppedCtx = croppedCanvas.getContext("2d");

  if (!croppedCtx) {
    return null;
  }

  // Set the size of the cropped canvas
  croppedCanvas.width = pixelCrop.width;
  croppedCanvas.height = pixelCrop.height;

  // Draw the cropped image onto the new canvas
  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  // As Base64 string
  // return croppedCanvas.toDataURL('image/jpeg');
  // data: FormData,
  //   croppedImg: string,
  //   canvasScaled: HTMLCanvasElement
  const prom = new Promise((resolve, reject) => {
    croppedCanvas.toBlob((file) => {
      const data = new FormData();
      data.append("avatar", file);

      resolve({
        data,
        croppedImg: croppedCanvas.toDataURL("image/jpeg"),
        canvasScaled: croppedCanvas,
      });
    }, "image/jpeg");
  });
  return prom;
}
